import {
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { useEffect } from 'react'
import {
  MeetingAttemptReservationResultMeetingReservation,
  ReserveMeetingAttemptRequestV2,
  UserAvailabilityApiClientV2,
} from '@chilipiper/api-client/src/user-availability'
import {
  ReserveTimeSlotParams,
  ReserveMeetingAttemptParams,
  CancelReservationSlotParams,
  CancelMeetingAttemptReservationParams,
} from '../types'
import {
  setMeetingAttemptReservation,
  setSlotReservation,
  cleanSessionStorage,
  getSlotReservation,
} from '../helpers'

type MeetingAttemptReservationOptions = {
  enabled?: boolean
  fallbackData?: MeetingAttemptReservationResultMeetingReservation
  payload?: ReserveMeetingAttemptRequestV2
}

interface ClaimMeetingAttemptReservationParams {
  tenantId?: string
  meetingAttemptId?: string
  options?: MeetingAttemptReservationOptions
}

const claimMeetingAttemptReservationQuery = ({
  tenantId,
  meetingAttemptId,
  options,
  userAvailabilityV2,
}: ClaimMeetingAttemptReservationParams & { userAvailabilityV2: UserAvailabilityApiClientV2 }) => {
  const payload = options?.payload ?? undefined
  return queryOptions({
    queryKey: [tenantId, meetingAttemptId, 'user-availability/meeting-attemp-reservation'],
    queryFn:
      tenantId && meetingAttemptId && payload && options?.enabled
        ? () =>
            userAvailabilityV2.claimAMeetingAttemptReservation(tenantId, meetingAttemptId, payload)
        : skipToken,
    placeholderData: options?.fallbackData,
    retry: false,
  })
}

export const useClaimMeetingAttemptReservation = (params: ClaimMeetingAttemptReservationParams) => {
  const { userAvailabilityV2 } = useApiClient()
  const result = useQuery(claimMeetingAttemptReservationQuery({ ...params, userAvailabilityV2 }))
  useEffect(() => {
    if (!result.isPlaceholderData && result.data?.type === 'SuccessfullyReserved') {
      setMeetingAttemptReservation(result.data)
      setSlotReservation(result.data.meetingReservation)
    }
  }, [result.data])

  return result
}

const useClaimMeetingAttemptReservationMutation = () => {
  const queryClient = useQueryClient()
  const { userAvailabilityV2 } = useApiClient()
  return useMutation({
    mutationFn: ({ tenantId, attemptId, payload }: ReserveMeetingAttemptParams) =>
      userAvailabilityV2.claimAMeetingAttemptReservation(tenantId, attemptId, payload),
    onSuccess: (data, params) => {
      if (data.type === 'SuccessfullyReserved') {
        setMeetingAttemptReservation(data)
        setSlotReservation(data.meetingReservation)
      }

      queryClient.setQueryData(
        claimMeetingAttemptReservationQuery({ ...params, userAvailabilityV2 }).queryKey,
        data
      )
    },
  })
}

export const useCancelMeetingAttemptReservation = () => {
  const queryClient = useQueryClient()
  const { userAvailability, userAvailabilityV2 } = useApiClient()
  return useMutation({
    mutationFn: ({
      tenantId,
      meetingAttemptId,
      reservationId,
    }: CancelMeetingAttemptReservationParams) =>
      userAvailability
        .cancelReservationOfMeetingAttempt(tenantId, meetingAttemptId, reservationId)
        .finally(() => {
          cleanSessionStorage()
        }),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: [claimMeetingAttemptReservationQuery({ ...params, userAvailabilityV2 }).queryKey],
      })
    },
  })
}

const useSlotReservationTrigger = () => {
  const { userAvailabilityV3 } = useApiClient()
  return useMutation({
    mutationFn: ({ tenantId, payload, feature }: ReserveTimeSlotParams) =>
      userAvailabilityV3
        .postApiV3PublicTenantTenantidUsersSlots(
          tenantId,
          payload,
          feature
            ? {
                feature: [feature],
              }
            : undefined
        )
        .then(data => {
          setSlotReservation(data)
          return data
        }),
  })
}

export const useReservationSlot = () => {
  const {
    mutateAsync: onReserveTimeSlot,
    isPending: isLoadingTimeSlotReservation,
    error: reserveTimeSlotError,
  } = useSlotReservationTrigger()
  const {
    mutateAsync: onReserveMeetingAttempt,
    isPending: isLoadingMeetingAttemptReservation,
    error: reserveMeetingAttemptError,
  } = useClaimMeetingAttemptReservationMutation()

  const resolveReservationSlot = async (
    data: ReserveTimeSlotParams | ReserveMeetingAttemptParams
  ) => {
    return 'attemptId' in data && data.attemptId
      ? onReserveMeetingAttempt(data).then(data => {
          if (data.type === 'SuccessfullyReserved') {
            return data.meetingReservation
          }

          const reservation = getSlotReservation()

          if (data.type === 'AlreadyReserved' && reservation) {
            return reservation
          }
        })
      : onReserveTimeSlot(data)
  }

  return {
    onReserveTimeSlot: resolveReservationSlot,
    error: reserveTimeSlotError || reserveMeetingAttemptError,
    isLoadingReservation: isLoadingTimeSlotReservation || isLoadingMeetingAttemptReservation,
  }
}

export const useCancelReservationSlot = () => {
  const { mutateAsync: onCancelMeetingAttemptReservation, isPending: isCancellingMeetingAttempt } =
    useCancelMeetingAttemptReservation()
  const { mutateAsync: onDeleteReservation, isPending: isDeletingReservation } =
    useDeleteReservationTrigger()

  const onCancelReservation = async (
    data: CancelReservationSlotParams | CancelMeetingAttemptReservationParams
  ) => {
    return 'meetingAttemptId' in data && data.meetingAttemptId
      ? onCancelMeetingAttemptReservation(data)
      : onDeleteReservation(data)
  }

  return {
    onCancelReservation,
    isCancellingReservation: isCancellingMeetingAttempt || isDeletingReservation,
  }
}

const useDeleteReservationTrigger = () => {
  const { userAvailability } = useApiClient()
  return useMutation({
    mutationFn: ({ tenantId, reservationId, feature }: CancelReservationSlotParams) => {
      const query = feature ? { feature: [feature] } : undefined
      return userAvailability
        .deleteTimeSlotReservation(tenantId, reservationId, query)
        .finally(() => {
          cleanSessionStorage()
        })
    },
  })
}
